import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Lightning,
  ArrowLeft,
  Trash,
  Megaphone,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Target,
  CurrencyEur,
  CalendarBlank,
  ArrowSquareOut,
  Globe,
  IdentificationBadge,
  ListBullets
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [showLog, setShowLog] = useState(false);

  const fetchCampaign = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/campaigns/${id}`);
      setCampaign(response.data);
    } catch (error) {
      console.error('Error fetching campaign:', error);
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCampaign();
  }, [fetchCampaign]);

  const handleDelete = async () => {
    if (!window.confirm('Sei sicuro di voler eliminare questa campagna?')) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/campaigns/${id}`);
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting campaign:', error);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return (
          <span data-testid="status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#E4FF1A]/10 text-[#E4FF1A] text-sm font-medium">
            <CheckCircle weight="fill" className="w-4 h-4" />
            Pubblicata (PAUSED su Meta)
          </span>
        );
      case 'draft':
        return (
          <span data-testid="status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 text-[#A1A1AA] text-sm font-medium">
            <Clock weight="fill" className="w-4 h-4" />
            Bozza
          </span>
        );
      case 'error':
        return (
          <span data-testid="status-badge" className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/10 text-red-400 text-sm font-medium">
            <XCircle weight="fill" className="w-4 h-4" />
            Errore
          </span>
        );
      default:
        return null;
    }
  };

  const getAdsManagerUrl = () => {
    if (!campaign?.meta_ad_account_id) return null;
    const accId = campaign.meta_ad_account_id.replace('act_', '');
    if (campaign.meta_campaign_id) {
      return `https://business.facebook.com/adsmanager/manage/campaigns?act=${accId}&selected_campaign_ids=${campaign.meta_campaign_id}`;
    }
    return `https://business.facebook.com/adsmanager/manage/campaigns?act=${accId}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05050A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#FF2A85] rounded-full animate-spin" />
      </div>
    );
  }

  if (!campaign) return null;

  const adsManagerUrl = getAdsManagerUrl();

  return (
    <div className="min-h-screen bg-[#05050A]">
      {/* Header */}
      <header className="glass border-b border-white/10 px-6 py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-button"
              onClick={() => navigate('/dashboard')}
              className="p-2 rounded-lg hover:bg-white/5 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF2A85] flex items-center justify-center neon-glow">
                <Lightning weight="fill" className="w-6 h-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Unbounded' }}>
                  {campaign.name}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {adsManagerUrl && (
              <a
                href={adsManagerUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="meta-ads-manager-link"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0055FF] hover:bg-[#0066FF] text-white rounded-lg text-sm font-medium transition-colors"
              >
                <ArrowSquareOut weight="bold" className="w-4 h-4" />
                Apri in Meta Ads Manager
              </a>
            )}
            <Button
              data-testid="delete-campaign"
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50"
            >
              <Trash className="w-4 h-4 mr-2" />
              Elimina
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Status & Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5">
            <p className="text-sm text-[#A1A1AA] mb-2">Stato</p>
            {getStatusBadge(campaign.status)}
          </div>
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5">
            <p className="text-sm text-[#A1A1AA] mb-2">Tipo</p>
            <div className="flex items-center gap-2">
              {campaign.campaign_type === 'lead_generation' ? (
                <>
                  <Users weight="fill" className="w-5 h-5 text-[#0055FF]" />
                  <span className="font-medium">Lead Generation</span>
                </>
              ) : (
                <>
                  <Megaphone weight="fill" className="w-5 h-5 text-purple-500" />
                  <span className="font-medium">Interazione</span>
                </>
              )}
            </div>
          </div>
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5">
            <p className="text-sm text-[#A1A1AA] mb-2">Budget</p>
            <div className="flex items-center gap-2">
              <CurrencyEur weight="fill" className="w-5 h-5 text-[#E4FF1A]" />
              <span className="font-medium">
                {'\u20AC'}{(campaign.budget_cents / 100).toFixed(2)}
                {campaign.daily_budget ? '/giorno' : ' totale'}
              </span>
            </div>
          </div>
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-5">
            <p className="text-sm text-[#A1A1AA] mb-2">Creata il</p>
            <div className="flex items-center gap-2">
              <CalendarBlank weight="fill" className="w-5 h-5 text-[#A1A1AA]" />
              <span className="font-medium">
                {new Date(campaign.created_at).toLocaleDateString('it-IT')}
              </span>
            </div>
          </div>
        </div>

        {/* Error message */}
        {campaign.error_message && (
          <div className="mb-8 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
            <p className="font-semibold mb-1">Errore nella pubblicazione:</p>
            <p className="text-sm break-all">{typeof campaign.error_message === 'string' ? campaign.error_message : JSON.stringify(campaign.error_message)}</p>
          </div>
        )}

        {/* Meta Account & IDs Section */}
        {(campaign.meta_campaign_id || campaign.meta_page_name || campaign.meta_ad_account_id) && (
          <div className="bg-[#0C0D14] border border-[#0055FF]/30 rounded-xl p-6 mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
                <IdentificationBadge weight="fill" className="w-5 h-5 text-[#0055FF]" />
                Dettagli Meta Ads
              </h2>
              {adsManagerUrl && (
                <a
                  href={adsManagerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#0055FF] hover:text-[#0066FF] flex items-center gap-1"
                >
                  Vedi su Meta <ArrowSquareOut weight="bold" className="w-3.5 h-3.5" />
                </a>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Page & Account */}
              <div className="space-y-3">
                {campaign.meta_page_name && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <Globe weight="fill" className="w-5 h-5 text-[#0055FF] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Pagina Facebook</p>
                      <p className="font-medium">{campaign.meta_page_name}</p>
                      {campaign.meta_page_id && <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">{campaign.meta_page_id}</p>}
                    </div>
                  </div>
                )}
                {campaign.meta_ad_account_id && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <IdentificationBadge weight="fill" className="w-5 h-5 text-[#E4FF1A] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Ad Account</p>
                      <p className="font-medium font-mono">{campaign.meta_ad_account_id}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Campaign & AdSet IDs */}
              <div className="space-y-3">
                {campaign.meta_campaign_id && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <Megaphone weight="fill" className="w-5 h-5 text-[#FF2A85] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Campagna Meta</p>
                      <p className="font-medium">{campaign.meta_campaign_name || campaign.name}</p>
                      <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">ID: {campaign.meta_campaign_id}</p>
                    </div>
                  </div>
                )}
                {campaign.meta_adset_id && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <Target weight="fill" className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Ad Set</p>
                      <p className="font-medium">{campaign.meta_adset_name || `${campaign.name} - AdSet`}</p>
                      <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">ID: {campaign.meta_adset_id}</p>
                    </div>
                  </div>
                )}
                {campaign.meta_lead_form_id && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <Users weight="fill" className="w-5 h-5 text-[#0055FF] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Lead Form</p>
                      <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">ID: {campaign.meta_lead_form_id}</p>
                    </div>
                  </div>
                )}
                {campaign.meta_ad_id && (
                  <div className="flex items-start gap-3 p-3 bg-black/30 rounded-lg">
                    <Megaphone weight="fill" className="w-5 h-5 text-[#E4FF1A] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#A1A1AA]">Inserzione (Ad)</p>
                      <p className="font-medium">{campaign.meta_ad_name || `${campaign.name} - Ad`}</p>
                      <p className="text-xs text-[#A1A1AA] font-mono mt-0.5">ID: {campaign.meta_ad_id}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Publish Log */}
            {campaign.publish_log && campaign.publish_log.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <button
                  data-testid="toggle-publish-log"
                  onClick={() => setShowLog(!showLog)}
                  className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
                >
                  <ListBullets weight="bold" className="w-4 h-4" />
                  {showLog ? 'Nascondi' : 'Mostra'} log pubblicazione ({campaign.publish_log.length} step)
                </button>
                {showLog && (
                  <div className="mt-3 space-y-2">
                    {campaign.publish_log.map((log, i) => (
                      <div key={i} className="p-3 bg-black/40 rounded-lg text-xs font-mono">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-block w-2 h-2 rounded-full ${log.status === 200 ? 'bg-green-400' : 'bg-red-400'}`} />
                          <span className="text-[#A1A1AA] uppercase font-bold">{log.step}</span>
                          <span className="text-[#A1A1AA]">— HTTP {log.status}</span>
                        </div>
                        <pre className="text-[#A1A1AA] whitespace-pre-wrap break-all max-h-32 overflow-y-auto">
                          {JSON.stringify(log.response, null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Creatives */}
          <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-6">
            <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Unbounded' }}>
              Creatività
            </h2>
            
            <Tabs defaultValue="1x1" className="w-full">
              <TabsList className="bg-black/40 border border-white/10 mb-4">
                <TabsTrigger value="1x1" className="data-[state=active]:bg-[#FF2A85]">
                  Feed 1:1
                </TabsTrigger>
                <TabsTrigger value="9x16" className="data-[state=active]:bg-[#FF2A85]">
                  Stories 9:16
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="1x1">
                {campaign.image_1x1_base64 ? (
                  <img
                    data-testid="creative-1x1"
                    src={`data:image/jpeg;base64,${campaign.image_1x1_base64}`}
                    alt="1:1 Preview"
                    className="w-full rounded-lg"
                  />
                ) : (
                  <div className="aspect-square bg-black/40 rounded-lg flex items-center justify-center">
                    <p className="text-[#A1A1AA]">Immagine non disponibile</p>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="9x16">
                {campaign.image_9x16_base64 ? (
                  <div className="flex justify-center">
                    <img
                      data-testid="creative-9x16"
                      src={`data:image/jpeg;base64,${campaign.image_9x16_base64}`}
                      alt="9:16 Preview"
                      className="max-h-[500px] rounded-lg"
                    />
                  </div>
                ) : (
                  <div className="aspect-[9/16] max-h-[500px] bg-black/40 rounded-lg flex items-center justify-center mx-auto">
                    <p className="text-[#A1A1AA]">Immagine non disponibile</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Details */}
          <div className="space-y-6">
            {/* Copy */}
            <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Unbounded' }}>
                Testi Annuncio
              </h2>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#A1A1AA] mb-1">Headline</p>
                  <p className="font-medium">{campaign.generated_headline}</p>
                </div>
                <div>
                  <p className="text-sm text-[#A1A1AA] mb-1">Testo Primario</p>
                  <p className="font-medium">{campaign.generated_primary_text}</p>
                </div>
              </div>
            </div>

            {/* Targeting */}
            <div className="bg-[#0C0D14] border border-white/10 rounded-xl p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
                <Target weight="fill" className="w-5 h-5 text-[#FF2A85]" />
                Targeting
              </h2>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-[#A1A1AA] mb-1">Età</p>
                  <p className="font-medium">
                    {campaign.target_audience?.age_min} - {campaign.target_audience?.age_max} anni
                  </p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] mb-1">Genere</p>
                  <p className="font-medium">
                    {campaign.target_audience?.genders?.length === 2 ? 'Tutti' : 
                     campaign.target_audience?.genders?.includes(1) ? 'Uomini' : 'Donne'}
                  </p>
                </div>
                <div>
                  <p className="text-[#A1A1AA] mb-1">Località</p>
                  <p className="font-medium">
                    {campaign.target_audience?.locations?.join(', ') || 'IT'}
                  </p>
                </div>
                {campaign.target_audience?.interests && (
                  <div>
                    <p className="text-[#A1A1AA] mb-1">Interessi</p>
                    <p className="font-medium">
                      {campaign.target_audience.interests.join(', ')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Lead Form Config */}
            {campaign.lead_form_config && (
              <div className="bg-[#0055FF]/5 border border-[#0055FF]/20 rounded-xl p-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Unbounded' }}>
                  <Users weight="fill" className="w-5 h-5 text-[#0055FF]" />
                  Modulo Lead
                </h2>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle weight="fill" className={`w-4 h-4 ${campaign.lead_form_config.include_email ? 'text-[#E4FF1A]' : 'text-[#A1A1AA]'}`} />
                    <span>Email</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle weight="fill" className={`w-4 h-4 ${campaign.lead_form_config.include_phone ? 'text-[#E4FF1A]' : 'text-[#A1A1AA]'}`} />
                    <span>Telefono</span>
                  </div>
                  {campaign.lead_form_config.qualifying_question && (
                    <div className="pt-2 border-t border-white/10 mt-2">
                      <p className="text-[#A1A1AA]">Domanda qualificante:</p>
                      <p className="font-medium">{campaign.lead_form_config.qualifying_question}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
